import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

// Independent Teacher Conflict Detection System
// This system analyzes uploaded Excel data for teacher allocation conflicts
// Completely separate from room allocation functionality

// Display names for each conflict type
export const TEACHER_CONFLICT_TYPE_LABELS = {
  teacher_double_booked: "Teacher double-booked",
  room_shared: "Room shared",
};

// Normalize Day/Time + EW/Time into "<day> <period>", e.g. "monday am".
// Returns null when the text does not name both a day and AM, PM or Eve.
const normalizeTimeSlot = (dayTime, ewTime) => {
  if (!dayTime && !ewTime) return null;

  // Extract day and time, combine them and remove parenthetical content
  let day = dayTime ? dayTime.toString().trim() : "";
  const time = ewTime ? ewTime.toString().trim() : "";

  // First step: Combine inputs and remove parenthetical content
  let combined = (day + " " + time)
    .trim()
    .replace(/\s*\([^)]*\)/g, "") // Remove anything in parentheses including parentheses
    .toLowerCase();

  // Clean up any double spaces created after removing parentheses
  combined = combined.replace(/\s+/g, " ").trim();

  // Verify the result has both a valid day and valid time period
  const validDayPattern = /\b(mon|tue|wed|thu|fri|sat|sun)\b/;
  const validTimePattern = /\b(am|pm|eve)\b/;

  // Extract just the day and time period if they exist
  const dayMatch = combined.match(validDayPattern);
  const timeMatch = combined.match(validTimePattern);

  // Both day and time period MUST be present
  if (!dayMatch || !timeMatch) {
    return null;
  }

  // Construct the standardized format with just the matched day and time
  const standardized = `${dayMatch[0]} ${timeMatch[0]}`;

  // Normalize standardized format for teacher conflict analysis
  const normalized = standardized
    .replace(/\bmon\b/g, "monday")
    .replace(/\btue\b/g, "tuesday")
    .replace(/\bwed\b/g, "wednesday")
    .replace(/\bthu\b/g, "thursday")
    .replace(/\bfri\b/g, "friday")
    .replace(/\bsat\b/g, "saturday")
    .replace(/\bsun\b/g, "sunday")
    // Keep am, pm and eve as is, don't convert to morning/afternoon/evening
    .replace(/\s+/g, " ")
    .trim();

  return normalized || null;
};

// The same person written with different capitals or spacing ("John  Smith", "john smith")
const normalizeTeacherName = (name) => name.replace(/\s+/g, " ").toLowerCase();

const addToGroup = (groups, key, allocation) => {
  if (!groups.has(key)) {
    groups.set(key, []);
  }
  groups.get(key).push(allocation);
};

// One name per person, in the spelling of their first row
const distinctTeachers = (allocations) => {
  const byKey = new Map();
  allocations.forEach((a) => {
    if (!byKey.has(a.teacherKey)) byKey.set(a.teacherKey, a.teacher);
  });
  return Array.from(byKey.values());
};

// Rows for one tutor, unit and room in the same slot are one session listed more than once
// (once per group, say), not a double booking. A row with no unit name cannot be matched
// to another, so it always counts as a session of its own.
const sessionKey = (allocation) => {
  const unit = String(allocation.unitName || "").replace(/\s+/g, " ").trim().toLowerCase();
  return unit ? `${unit}|${allocation.room}` : `row|${allocation.rowNumber}`;
};

// The slot as written in the sheet, e.g. "Thu AM"
const slotLabel = (allocation) =>
  `${allocation.originalDayTime || ""} ${allocation.originalEwTime || ""}`.trim();

// "all in room 101", "rooms 101, 102" or "rooms 101, unallocated"
const describeRooms = (allocations) => {
  const rooms = Array.from(new Set(allocations.map((a) => a.room || "unallocated")));
  if (rooms.length > 1) return `rooms ${rooms.join(", ")}`;
  return rooms[0] === "unallocated" ? "no room allocated" : `all in room ${rooms[0]}`;
};

/**
 * Find teacher conflicts in the loaded rows. Rows are compared within one day and period
 * (Day/Time + EW/Time, e.g. "Mon AM"), and there are two kinds of conflict:
 * - teacher_double_booked: the same tutor is in more than one session, in any room,
 *   including rows that have no room allocated yet. Rows with the same tutor, unit and
 *   room are one combined session, not a double booking.
 * - room_shared: one room has more than one different tutor.
 * Tutor and unit names are compared ignoring capitals and repeated spaces.
 *
 * @param {{data: Array<Object>}} excelData - rows as built by parseExcelFile
 * @returns {{
 *   hasConflicts: boolean,
 *   totalConflicts: number,
 *   conflicts: Array<Object>,
 *   combinedSessions: Array<{teacher: string, unitName: string, room: string,
 *     timeSlotLabel: string, rows: Array<number>}>,
 *   rowsChecked: number,
 *   skippedRows: Array<number>,
 *   summary: string,
 *   analysisDate: string
 * }} combinedSessions lists the rows that were not flagged because they are one session;
 *   skippedRows holds the sheet row numbers that have a tutor but no readable day and period
 */
export const analyzeTeacherConflicts = (excelData) => {
  if (!excelData || !excelData.data || !Array.isArray(excelData.data)) {
    throw new Error("Invalid Excel data provided for teacher conflict analysis");
  }

  const teacherSlotMap = new Map(); // "teacher|slot" -> allocations
  const roomSlotMap = new Map(); // "room|slot" -> allocations
  const skippedRows = [];
  let rowsChecked = 0;

  excelData.data.forEach((row, index) => {
    // Skip section headers and rows with no tutor - there is no one to double-book
    const teacher = String(row.tutor_name || "").trim();
    if (row.is_section_header || !teacher) {
      return;
    }

    const timeSlot = normalizeTimeSlot(row.day_time, row.ew_time);
    if (!timeSlot) {
      skippedRows.push(row.row_index);
      return;
    }
    rowsChecked += 1;

    const allocation = {
      rowIndex: index,
      rowNumber: row.row_index,
      teacher: teacher,
      teacherKey: normalizeTeacherName(teacher),
      room: String(row.room_auto_allo || "").trim(),
      timeSlot: timeSlot,
      originalDayTime: row.day_time,
      originalEwTime: row.ew_time,
      unitName: row.unit_name,
      vleClassName: row.vle_class_name,
      studentsCount: row.students_coming || 0,
      itemNo: row.item_no,
      unitNo: row.unit_no,
    };

    addToGroup(teacherSlotMap, `${allocation.teacherKey}|${timeSlot}`, allocation);
    // A row with no room can still double-book its tutor, but cannot share a room
    if (allocation.room) {
      addToGroup(roomSlotMap, `${allocation.room}|${timeSlot}`, allocation);
    }
  });

  const conflicts = [];
  const combinedSessions = [];

  // One tutor in more than one session in the same slot = DOUBLE-BOOKED, whatever the rooms
  teacherSlotMap.forEach((allocations) => {
    if (allocations.length < 2) return;

    if (new Set(allocations.map(sessionKey)).size > 1) {
      conflicts.push(buildConflict("teacher_double_booked", allocations, conflicts.length + 1));
    } else {
      const first = allocations[0];
      combinedSessions.push({
        teacher: first.teacher,
        unitName: first.unitName,
        room: first.room,
        timeSlotLabel: slotLabel(first),
        rows: allocations.map((a) => a.rowNumber),
      });
    }
  });

  // Different tutors in the same room and slot = ROOM SHARED
  roomSlotMap.forEach((allocations) => {
    if (new Set(allocations.map((a) => a.teacherKey)).size > 1) {
      conflicts.push(buildConflict("room_shared", allocations, conflicts.length + 1));
    }
  });

  const doubleBookings = conflicts.filter((c) => c.type === "teacher_double_booked").length;

  return {
    hasConflicts: conflicts.length > 0,
    totalConflicts: conflicts.length,
    conflicts: conflicts,
    combinedSessions: combinedSessions,
    rowsChecked: rowsChecked,
    skippedRows: skippedRows,
    summary:
      conflicts.length > 0
        ? `${conflicts.length} teacher conflict(s) detected: ${doubleBookings} double-booked teacher(s), ${
            conflicts.length - doubleBookings
          } shared room(s).`
        : "No teacher allocation conflicts found.",
    analysisDate: new Date().toISOString(),
  };
};

const buildConflict = (type, allocations, number) => {
  const first = allocations[0];
  const timeSlotLabel = slotLabel(first);
  const rooms = Array.from(new Set(allocations.map((a) => a.room).filter(Boolean)));
  const teachers = distinctTeachers(allocations);
  const isDoubleBooking = type === "teacher_double_booked";

  return {
    id: `teacher_conflict_${number}`,
    type: type,
    rooms: rooms,
    timeSlot: first.timeSlot,
    timeSlotLabel: timeSlotLabel,
    conflictingAllocations: allocations,
    severity: "high",
    description: isDoubleBooking
      ? `${teachers[0]} is on ${allocations.length} rows at ${timeSlotLabel} (${describeRooms(
          allocations
        )})`
      : `Multiple teachers (${teachers.join(", ")}) assigned to room ${rooms[0]} at ${timeSlotLabel}`,
    affectedRows: allocations.map((a) => a.rowNumber),
    affectedItems: allocations.map((a) => a.itemNo || "N/A"), // Include item numbers for display
    conflictingTeachers: teachers,
  };
};

// Function to export teacher conflict report as professionally formatted Excel
export const exportTeacherConflictReport = (
  conflictAnalysis,
  originalFileName = "teacher_conflicts_report"
) => {
  if (!conflictAnalysis || !conflictAnalysis.hasConflicts) {
    console.log("No teacher conflicts found to export");
    return { success: false, message: "No conflicts to export" };
  }

  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Summary Sheet Data
    const summaryData = [
      ["Teacher Conflict Analysis Report"],
      ["Generated on:", new Date().toLocaleString()],
      ["Total Conflicts:", conflictAnalysis.totalConflicts],
      [""],
      [
        "Conflict #",
        "Type",
        "Room(s)",
        "Time Slot",
        "Conflicting Teachers",
        "Students Affected",
        "Affected Rows/Items",
        "Severity",
        "Description",
      ],
    ];

    // Add conflict data
    conflictAnalysis.conflicts.forEach((conflict, index) => {
      const totalStudents = conflict.conflictingAllocations.reduce(
        (sum, alloc) => sum + (parseInt(alloc.studentsCount) || 0),
        0
      );

      // Format rows and items for display
      const rowsAndItems = conflict.affectedRows
        .map((row, idx) => {
          const item = conflict.affectedItems[idx] || "N/A";
          return `Row ${row} (Item ${item})`;
        })
        .join(", ");

      summaryData.push([
        index + 1,
        TEACHER_CONFLICT_TYPE_LABELS[conflict.type],
        conflict.rooms.join(", ") || "Unallocated",
        conflict.timeSlotLabel,
        conflict.conflictingTeachers.join(" vs "),
        totalStudents,
        rowsAndItems,
        conflict.severity || "Medium",
        conflict.description,
      ]);
    });

    // Create summary worksheet
    const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);

    // Auto-calculate column widths based on content with better sizing
    const summaryRange = XLSX.utils.decode_range(summaryWS["!ref"]);
    const colWidths = [];

    // Calculate exact width for each column based on actual content
    for (let col = summaryRange.s.c; col <= summaryRange.e.c; col++) {
      let maxWidth = 10; // minimum width

      for (let row = summaryRange.s.r; row <= summaryRange.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = summaryWS[cellRef];
        if (cell && cell.v) {
          const cellValue = String(cell.v);
          let cellWidth = cellValue.length;

          // Add extra space for specific content types
          if (row === 0 && cellValue.includes("Teacher Conflict")) cellWidth += 12; // More space for title
          if (row === 4) cellWidth += 4; // Headers get extra space
          if (cellValue.includes(" vs ")) cellWidth += 3; // Teacher names
          if (cellValue.includes("Multiple teachers")) cellWidth += 5; // Description text

          maxWidth = Math.max(maxWidth, cellWidth);
        }
      }

      // Add minimal padding and set exact limits per column
      let finalWidth = maxWidth + 1; // Minimal padding

      // Exact column-specific limits based on actual content
      const limits = {
        0: 12, // Conflict # (just enough for 2 digits)
        1: 24, // Type ("Teacher double-booked")
        2: 20, // Room(s) (one room, or a list for a double-booked teacher)
        3: 20, // Time Slot (exact for "Sat AM", "Tue AM" etc)
        4: 40, // Conflicting Teachers (exact for teacher names with " vs ")
        5: 15, // Students Affected (numbers only)
        6: 25, // Affected Rows/Items (e.g., "Row 5 (Item 1), Row 6 (Item 2)")
        7: 10, // Severity (just "high")
        8: 80, // Description (reduced from 120)
      };
      const maxLimit = limits[col] || 40;

      finalWidth = Math.min(finalWidth, maxLimit);
      colWidths.push({ wch: finalWidth });
    }

    summaryWS["!cols"] = colWidths;

    // Merge title cell A1 across multiple columns for better visibility
    summaryWS["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }]; // Merge A1 to D1

    // Style title area (A1:B3) - make all BOLD with proper formatting
    for (let row = 0; row <= 2; row++) {
      for (let col = summaryRange.s.c; col <= summaryRange.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!summaryWS[cellRef]) summaryWS[cellRef] = { t: "s", v: "" };

        // Special formatting for title row
        if (row === 0) {
          summaryWS[cellRef].s = {
            font: { bold: true, size: 16, color: { rgb: "2F4F4F" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "medium", color: { rgb: "333333" } },
              bottom: { style: "medium", color: { rgb: "333333" } },
              left: { style: "medium", color: { rgb: "333333" } },
              right: { style: "medium", color: { rgb: "333333" } },
            },
          };
        } else {
          // Rows 1 and 2 (Generated on, Total Conflicts)
          summaryWS[cellRef].s = {
            font: { bold: true, size: 11, color: { rgb: "2F4F4F" } },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "DDDDDD" } },
              bottom: { style: "thin", color: { rgb: "DDDDDD" } },
              left: { style: "thin", color: { rgb: "DDDDDD" } },
              right: { style: "thin", color: { rgb: "DDDDDD" } },
            },
          };
        }
      }
    }

    // Style header row (row 5, index 4) - make BOLD with proper background
    for (let col = summaryRange.s.c; col <= summaryRange.e.c; col++) {
      const headerCell = XLSX.utils.encode_cell({ r: 4, c: col });
      if (!summaryWS[headerCell]) summaryWS[headerCell] = { t: "s", v: "" };
      summaryWS[headerCell].s = {
        fill: { fgColor: { rgb: "FF4472C4" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, size: 11 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "medium", color: { rgb: "000000" } },
          bottom: { style: "medium", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }

    // Use distinct colors for each conflict group
    const alternatingColors = [
      "F2F2F2", // Light gray
      "FFFFFF", // White
    ];

    // Get conflict number from the first column to determine color
    for (let row = 5; row <= summaryRange.e.r; row++) {
      const conflictCell = summaryWS[XLSX.utils.encode_cell({ r: row, c: 0 })];
      const conflictNum = conflictCell && conflictCell.v ? Number(conflictCell.v) : 0;
      const colorIndex = (conflictNum - 1) % alternatingColors.length;
      const bgColor = alternatingColors[colorIndex];

      for (let col = summaryRange.s.c; col <= summaryRange.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!summaryWS[cellRef]) summaryWS[cellRef] = { t: "s", v: "" };

        // Force override any existing styling
        summaryWS[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
            left: { style: "thin", color: { rgb: "CCCCCC" } },
            right: { style: "thin", color: { rgb: "CCCCCC" } },
          },
          alignment: { vertical: "center", wrapText: true },
          font: { size: 10 },
        };
      }
    }

    // Detailed Sheet Data
    const detailedData = [
      ["Detailed Teacher Conflict Breakdown"],
      [""],
      [
        "Conflict #",
        "Row #",
        "Item No",
        "Unit No",
        "Teacher",
        "Unit Name",
        "VLE Class",
        "Students",
        "Room",
        "Day/Time",
        "EW/Time",
      ],
    ];

    // Add detailed conflict data
    conflictAnalysis.conflicts.forEach((conflict, conflictIndex) => {
      conflict.conflictingAllocations.forEach((allocation, allocIndex) => {
        detailedData.push([
          conflictIndex + 1,
          allocation.rowNumber || "",
          allocation.itemNo || "",
          allocation.unitNo || "",
          allocation.teacher || "",
          allocation.unitName || "",
          allocation.vleClassName || "",
          allocation.studentsCount || "",
          allocation.room || "",
          allocation.originalDayTime || "",
          allocation.originalEwTime || "",
        ]);
      });
    });

    // Create detailed worksheet
    const detailedWS = XLSX.utils.aoa_to_sheet(detailedData);

    // Auto-calculate column widths for detailed sheet with better sizing
    const detailedRange = XLSX.utils.decode_range(detailedWS["!ref"]);
    const detailedColWidths = [];

    // Calculate exact width for detailed sheet columns
    for (let col = detailedRange.s.c; col <= detailedRange.e.c; col++) {
      let maxWidth = 8;

      for (let row = detailedRange.s.r; row <= detailedRange.e.r; row++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = detailedWS[cellRef];
        if (cell && cell.v) {
          const cellValue = String(cell.v);
          let cellWidth = cellValue.length;

          if (row === 0 && cellValue.includes("Detailed")) cellWidth += 5; // Title
          if (row === 2) cellWidth += 3; // Headers get extra space

          maxWidth = Math.max(maxWidth, cellWidth);
        }
      }

      // Add padding and set reasonable max limits
      let finalWidth = maxWidth + 2;
      const detailedLimits = {
        0: 12,
        1: 10,
        2: 12,
        3: 12,
        4: 40,
        5: 60,
        6: 25,
        7: 12,
        8: 15,
        9: 20,
        10: 20,
      };
      const maxLimit = detailedLimits[col] || 40;

      finalWidth = Math.min(finalWidth, maxLimit);
      detailedColWidths.push({ wch: finalWidth });
    }

    detailedWS["!cols"] = detailedColWidths;

    // Style title in detailed sheet - make BOLD
    const detailedTitleCell = detailedWS["A1"];
    if (detailedTitleCell) {
      detailedTitleCell.s = {
        font: { bold: true, size: 16, color: { rgb: "2F4F4F" } },
        alignment: { horizontal: "center" },
      };
    }

    // Style header row in detailed sheet - make BOLD
    for (let col = detailedRange.s.c; col <= detailedRange.e.c; col++) {
      const headerCell = XLSX.utils.encode_cell({ r: 2, c: col });
      if (!detailedWS[headerCell]) detailedWS[headerCell] = { t: "s", v: "" };
      detailedWS[headerCell].s = {
        fill: { fgColor: { rgb: "FF70AD47" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, size: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "medium", color: { rgb: "000000" } },
          bottom: { style: "medium", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }

    // Apply alternating colors to detailed data rows by conflict group
    let currentConflict = -1;
    for (let row = 3; row <= detailedRange.e.r; row++) {
      const conflictNumCell = detailedWS[XLSX.utils.encode_cell({ r: row, c: 0 })];
      const conflictNum = conflictNumCell ? conflictNumCell.v : null;

      if (conflictNum !== currentConflict) {
        currentConflict = conflictNum;
      }

      const colorIndex = (currentConflict - 1) % alternatingColors.length;
      const bgColor = alternatingColors[colorIndex];

      for (let col = detailedRange.s.c; col <= detailedRange.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!detailedWS[cellRef]) detailedWS[cellRef] = { t: "s", v: "" };
        detailedWS[cellRef].s = {
          fill: { fgColor: { rgb: bgColor } },
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
            left: { style: "thin", color: { rgb: "CCCCCC" } },
            right: { style: "thin", color: { rgb: "CCCCCC" } },
          },
          alignment: { vertical: "center" },
        };
      }
    }

    // Add worksheets to workbook
    XLSX.utils.book_append_sheet(workbook, summaryWS, "Conflict Summary");
    XLSX.utils.book_append_sheet(workbook, detailedWS, "Detailed Breakdown");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `${originalFileName}_formatted.xlsx`;
    saveAs(blob, fileName);

    console.log(`✅ Teacher conflict report exported successfully: ${fileName}`);

    return {
      success: true,
      message: `Exported professionally formatted report: ${fileName}`,
      filesGenerated: [fileName],
      conflictsFound: conflictAnalysis.totalConflicts,
    };
  } catch (error) {
    console.error("Error exporting teacher conflict report:", error);
    return {
      success: false,
      message: `Export failed: ${error.message}`,
    };
  }
};

/**
 * Summary counts for the stat cards.
 * @param {Object|null} conflictAnalysis - result of analyzeTeacherConflicts
 * @returns {{totalConflicts: number, doubleBookings: number, sharedRooms: number,
 *   totalTeachersAffected: number, totalRoomsAffected: number, totalTimeSlotsAffected: number}}
 */
export const getTeacherConflictStats = (conflictAnalysis) => {
  const stats = {
    totalConflicts: 0,
    doubleBookings: 0,
    sharedRooms: 0,
    totalTeachersAffected: 0,
    totalRoomsAffected: 0,
    totalTimeSlotsAffected: 0,
  };
  if (!conflictAnalysis || !conflictAnalysis.hasConflicts) {
    return stats;
  }

  const teachers = new Set();
  const rooms = new Set();
  const timeSlots = new Set();

  conflictAnalysis.conflicts.forEach((conflict) => {
    if (conflict.type === "teacher_double_booked") {
      stats.doubleBookings += 1;
    } else {
      stats.sharedRooms += 1;
    }
    // Count people, not spellings
    conflict.conflictingAllocations.forEach((allocation) => teachers.add(allocation.teacherKey));
    conflict.rooms.forEach((room) => rooms.add(room));
    timeSlots.add(conflict.timeSlot);
  });

  stats.totalConflicts = conflictAnalysis.totalConflicts;
  stats.totalTeachersAffected = teachers.size;
  stats.totalRoomsAffected = rooms.size;
  stats.totalTimeSlotsAffected = timeSlots.size;
  return stats;
};
